import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import { getSupabaseClient, type Session } from "@/services/supabase/client";
import type { AczonContext, AuthContextValue, AuthState } from "@/services/supabase/types";
import { apiRequest, setTokenProvider } from "@/services/http/client";

const STORAGE_KEY_PROJECT = "aczon_selected_project_id";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const initialState: AuthState = {
  session: null,
  user: null,
  aczonContext: null,
  loading: true,
  aczonDenied: false,
  error: null,
};

function friendlyError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login") || lower.includes("invalid credentials")) {
    return "Invalid email or password. Please verify your credentials and try again.";
  }
  if (lower.includes("email not confirmed")) {
    return "Your email has not been confirmed. Please contact your administrator.";
  }
  if (lower.includes("rate limit") || lower.includes("overruled")) {
    return "Too many attempts. Please wait a moment before trying again.";
  }
  return "Sign-in failed. Please verify your credentials and try again.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(
    () => (typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY_PROJECT) : null)
  );

  const fetchContext = useCallback(async (session: Session): Promise<{ context: AczonContext | null; denied: boolean }> => {
    try {
      const context = await apiRequest<AczonContext>("/context", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      return { context, denied: false };
    } catch {
      return { context: null, denied: true };
    }
  }, []);

  const initialize = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();

      setTokenProvider(async () => {
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token || null;
      });

      const { data } = await supabase.auth.getSession();
      if (data.session) {
        const { context, denied } = await fetchContext(data.session);
        setState({
          session: data.session,
          user: data.session.user,
          aczonContext: context,
          loading: false,
          aczonDenied: denied,
          error: null,
        });
      } else {
        setState({ ...initialState, loading: false });
      }
    } catch {
      setState({ ...initialState, loading: false });
    }
  }, [fetchContext]);

  useEffect(() => {
    initialize();
    const supabase = getSupabaseClient();
    const { data: subscription } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      // PASSWORD_RECOVERY is handled by ResetPasswordPage's own listener.
      // If we intercept it here, the session would be treated as a normal
      // login and the user would be routed away from /reset-password.
      if (event === "PASSWORD_RECOVERY") {
        return;
      }
      if (session) {
        setState((prev) => ({ ...prev, session, user: session.user, loading: true }));
        const { context, denied } = await fetchContext(session);
        setState({
          session,
          user: session.user,
          aczonContext: context,
          loading: false,
          aczonDenied: denied,
          error: null,
        });
      } else {
        setState({ ...initialState, loading: false });
      }
    });
    return () => subscription.subscription.unsubscribe();
  }, [initialize, fetchContext]);

  const signIn = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setState({ ...initialState, loading: false, error: friendlyError(error.message) });
        throw error;
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message) {
        setState((prev) => ({ ...prev, loading: false, error: friendlyError(err.message) }));
      }
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    localStorage.removeItem(STORAGE_KEY_PROJECT);
    setCurrentProjectId(null);
    setState({ ...initialState, loading: false });
  }, []);

  const refreshContext = useCallback(async () => {
    if (!state.session) return;
    setState((prev) => ({ ...prev, loading: true }));
    const { context, denied } = await fetchContext(state.session);
    setState((prev) => ({ ...prev, aczonContext: context, aczonDenied: denied, loading: false }));
  }, [state.session, fetchContext]);

  const setProjectId = useCallback((projectId: string) => {
    localStorage.setItem(STORAGE_KEY_PROJECT, projectId);
    setCurrentProjectId(projectId);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    currentProjectId,
    signIn,
    signOut,
    refreshContext,
    setProjectId,
  }), [state, currentProjectId, signIn, signOut, refreshContext, setProjectId]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue | undefined {
  return useContext(AuthContext);
}
